import React from 'react';
import { StepProps } from '@/types/consultant-registration.types';
import { useLanguage } from '@/contexts/LanguageContext';
import { Checkbox } from '@/components/ui/checkbox';
import { ExternalLink } from 'lucide-react';

export function StepFour({ formData, updateFormData, errors, clearError }: StepProps) {
  const { language } = useLanguage();

  const translations = {
    en: {
      stepTitle: "Step 4: Terms & Conditions",
      termsLabel: "Terms and Conditions",
      viewFullTerms: "View Full Terms & Conditions",
      termsDescription: "Please read and accept our Terms and Conditions before proceeding.",
      acceptTerms: "I have read and accept the Terms and Conditions of AcquizGroup, including Annex E specific to Internal Partners and Consultants.",
      backgroundCheck: "I authorize AcquizGroup to conduct a background check as part of the verification process.",
      required: "This field is required"
    },
    fr: {
      stepTitle: "Étape 4 : Termes et Conditions",
      termsLabel: "Termes et Conditions",
      viewFullTerms: "Voir les Termes et Conditions complets",
      termsDescription: "Veuillez lire et accepter nos Termes et Conditions avant de continuer.",
      acceptTerms: "J'ai lu et j'accepte les Termes et Conditions d'AcquizGroup, incluant l'Annexe E spécifique aux Partenaires Internes et Consultants.",
      backgroundCheck: "J'autorise AcquizGroup à effectuer une vérification des antécédents dans le cadre du processus de vérification.",
      required: "Ce champ est requis"
    }
  };

  const t = translations[language];

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-[#1D314D]">{t.stepTitle}</h2>
        <p className="text-gray-600 mt-2">{t.termsDescription}</p>
      </div>

      {/* Link to Full Terms */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[#1D314D]">
            {t.termsLabel} <span className="text-red-500">*</span>
          </label>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-3 bg-[#2C3E50] text-white rounded-lg hover:bg-[#1D314D] transition-colors font-medium"
          >
            {t.viewFullTerms}
            <ExternalLink size={18} />
          </a>
        </div>
      </div>

      {/* Terms Acceptance Checkbox */}
      <div className="space-y-2">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              id="termsAccepted"
              checked={formData.termsAccepted}
              onCheckedChange={(checked) => {
                updateFormData({ termsAccepted: !!checked });
                clearError('termsAccepted');
              }}
              className="mt-1"
            />
            <span className="text-sm text-gray-700 leading-relaxed">
              {t.acceptTerms}
            </span>
          </label>
        </div>
        {errors.termsAccepted && (
          <p className="text-red-500 text-sm">{errors.termsAccepted}</p>
        )}
      </div>

      {/* Background Check Authorization */}
      <div className="space-y-2">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              id="backgroundCheckAuth"
              checked={formData.backgroundCheckAuth}
              onCheckedChange={(checked) => {
                updateFormData({ backgroundCheckAuth: !!checked });
                clearError('backgroundCheckAuth');
              }}
              className="mt-1"
            />
            <span className="text-sm text-gray-700 leading-relaxed">
              {t.backgroundCheck}
            </span>
          </label>
        </div>
        {errors.backgroundCheckAuth && (
          <p className="text-red-500 text-sm">{errors.backgroundCheckAuth}</p>
        )}
      </div>
    </div>
  );
}
